import React from 'react';
import { Phone, MessageSquare, Mail, UserPlus } from 'lucide-react';

const digits = (v) => String(v || '').replace(/[^\d+]/g, '');

const Action = ({ href, Icon, label, tone, testId, onClick }) => href ? (
  <a href={href} onClick={onClick} data-testid={testId} aria-label={label}
    className={`flex-1 inline-flex flex-col items-center justify-center gap-1 rounded-2xl py-2.5 text-[11px] font-bold active:scale-95 transition-transform ${tone}`}>
    <Icon className="w-[18px] h-[18px]" /> {label}
  </a>
) : (
  <span className="flex-1 inline-flex flex-col items-center justify-center gap-1 rounded-2xl py-2.5 text-[11px] font-bold bg-slate-50 text-slate-300" aria-disabled="true">
    <Icon className="w-[18px] h-[18px]" /> {label}
  </span>
);

const esc = (v) => String(v || '').replace(/([\\,;])/g, '\\$1').replace(/\n/g, '\\n');

const saveContact = (a, name, tel) => {
  const lines = ['BEGIN:VCARD', 'VERSION:3.0', `N:${esc(a.last_name)};${esc(a.first_name || name)};;;`, `FN:${esc(name)}`];
  if (tel) lines.push(`TEL;TYPE=CELL:${tel}`);
  if (a.email) lines.push(`EMAIL;TYPE=INTERNET:${a.email}`);
  lines.push('NOTE:Derwent client', 'END:VCARD');
  const blob = new Blob([lines.join('\r\n')], { type: 'text/vcard;charset=utf-8' });
  const file = new File([blob], `${name.replace(/[^\w ]+/g, '').trim() || 'contact'}.vcf`, { type: 'text/vcard' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url; link.download = file.name;
  document.body.appendChild(link); link.click(); link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

// Opens the Outlook app compose screen; falls back to the default mail app if Outlook isn't installed.
const openOutlook = (e, email) => {
  e.preventDefault();
  let left = false;
  const onHide = () => { if (document.hidden) left = true; };
  document.addEventListener('visibilitychange', onHide);
  window.location.href = `ms-outlook://compose?to=${encodeURIComponent(email)}`;
  setTimeout(() => {
    document.removeEventListener('visibilitychange', onHide);
    if (!left && !document.hidden) window.location.href = `mailto:${email}`;
  }, 1400);
};

// Tap-to-call / text / email for each applicant — phones & tablets only (desktop keeps the copy pills).
export const MobileContactBar = ({ applicants = [] }) => {
  const people = applicants.filter((a) => a.email || a.mobile);
  if (!people.length) return null;
  return (
    <div className="xl:hidden w-full space-y-2.5" data-testid="obd-mobile-contact">
      {people.map((a) => {
        const tel = digits(a.mobile);
        const name = [a.first_name, a.last_name].filter(Boolean).join(' ') || a.name || 'Applicant';
        return (
          <div key={a.id} className="rounded-2xl border border-slate-200/80 bg-slate-50/60 p-2.5" data-testid={`obd-mobile-contact-${a.id}`}>
            <p className="px-1 pb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 truncate">{name}</p>
            <div className="flex gap-2">
              <Action href={tel && `tel:${tel}`} Icon={Phone} label="Call" tone="bg-emerald-500 text-white shadow-sm shadow-emerald-500/30" testId={`obd-tap-call-${a.id}`} />
              <Action href={tel && `sms:${tel}`} Icon={MessageSquare} label="Text" tone="bg-sky-500 text-white shadow-sm shadow-sky-500/30" testId={`obd-tap-text-${a.id}`} />
              <Action href={a.email && `mailto:${a.email}`} Icon={Mail} label="Email" onClick={(e) => openOutlook(e, a.email)} tone="bg-[#0F6CBD] text-white shadow-sm shadow-sky-900/20" testId={`obd-tap-email-${a.id}`} />
              <button type="button" onClick={() => saveContact(a, name, tel)} data-testid={`obd-save-contact-${a.id}`} aria-label="Save contact"
                className="flex-1 inline-flex flex-col items-center justify-center gap-1 rounded-2xl py-2.5 text-[11px] font-bold bg-white text-slate-800 ring-1 ring-slate-200 shadow-sm active:scale-95 transition-transform">
                <UserPlus className="w-[18px] h-[18px]" /> Save
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
